import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Search_RequestBody } from '@opensearch-project/opensearch/api';
import { useRef } from 'react';

import { useIsServerSide, useSelectedStore } from '@/hooks';
import { useEcomStoreSelector } from '@/data';
import { EnumCustomerType, TreezStore } from '@/types';
import { SearchResponseBody } from 'opensearch/services/search';

import { useSearchService } from '../services';
import {
  buildProductsSearchQueryKey,
  PRODUCTS_SEARCH_STALE_TIME,
} from '../utils/queryKey';

const useSearch = ({
  searchBody,
  id,
  selectedCustomerType,
  selectedStore,
  initialSearchResponse,
}: {
  searchBody: Search_RequestBody;
  id?: string;
  selectedCustomerType?: EnumCustomerType;
  selectedStore?: TreezStore;
  initialSearchResponse?: SearchResponseBody;
}) => {
  const { isServerSide } = useIsServerSide();
  const { store } = useSelectedStore();
  const storeToUse = store || selectedStore;
  const { customer_type, provider_inventory_location_id } =
    useEcomStoreSelector(['customer_type', 'provider_inventory_location_id']);
  const customerTypeToUse = isServerSide ? selectedCustomerType : customer_type;

  const { search } = useSearchService({
    selectedStore: storeToUse,
    selectedCustomerType: customerTypeToUse,
  });

  const storeKey = store?.short_name || selectedStore?.short_name;
  const customerTypeKey = customer_type || selectedCustomerType;
  const inventoryKey = provider_inventory_location_id ?? '';

  const queryKey = buildProductsSearchQueryKey({
    id,
    storeName: storeKey,
    customerType: customerTypeKey,
    inventoryLocationId: inventoryKey,
    searchBody,
  });

  // initialData must only seed the SSR/prefetch query key. Applying it to every
  // key (with initialDataUpdatedAt + staleTime) reuses the original hits when
  // filters change and blocks refetch for PRODUCTS_SEARCH_STALE_TIME.
  const initialQueryKeyRef = useRef<string | null>(null);
  const initialDataUpdatedAtRef = useRef<number | null>(null);
  if (initialSearchResponse && initialQueryKeyRef.current === null) {
    initialQueryKeyRef.current = JSON.stringify(queryKey);
    initialDataUpdatedAtRef.current = Date.now();
  }
  const shouldUseInitialData =
    !!initialSearchResponse &&
    initialQueryKeyRef.current === JSON.stringify(queryKey);

  //TODO: change this to an infinite query
  const { data, isLoading, error, refetch, status, isFetching } = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await search({ searchBody });
      return result;
    },
    placeholderData: keepPreviousData,
    staleTime: PRODUCTS_SEARCH_STALE_TIME,
    // Without initialDataUpdatedAt, RQ treats initialData as stale and sets
    // isFetching=true on first paint → CollectionTreez shows placeholders.
    ...(shouldUseInitialData
      ? {
          initialData: initialSearchResponse,
          initialDataUpdatedAt: initialDataUpdatedAtRef.current ?? undefined,
        }
      : {}),
  });

  return {
    data,
    isLoading,
    error,
    refetch,
    status,
    isFetching,
  };
};

export { useSearch };
